import React from 'react';
import { Stack } from '@mui/material';
import { SectionLabel } from 'components/UI/SectionLabel';
import { UseFormReturn } from 'react-hook-form';
import { genderOptions } from 'settings';
import { FieldRow } from 'components/Fields/FieldRow';

interface FormValues {
  first_name: string;
  last_name: string;
  username: string | null;
  birthday: Date | null;
  phone: string | null;
  country: string | null;
  gender: string | null;
  email: string | null;
  current_password?: string;
  new_password?: string;
}

interface GeneralInfoProps {
  methods: UseFormReturn<FormValues>;
  countriesState: any[];
  onEditField: (field: { key: keyof FormValues | 'password' | 'name'; title: string }) => void;
}

/**
 * General profile fields, rendered as read-rows. Each row opens a per-field edit
 * drawer (EditFieldModal) on tap and saves that field on its own — the single
 * interaction model shared across the whole profile page (matches the design).
 */
export const GeneralInfo: React.FC<GeneralInfoProps> = ({ methods, countriesState, onEditField }) => {
  const { watch } = methods;

  const firstName = watch('first_name');
  const lastName = watch('last_name');
  const username = watch('username');
  const birthday = watch('birthday');
  const gender = watch('gender');
  const country = watch('country');
  const phone = watch('phone');

  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const genderLabel = genderOptions.find(o => o.value === gender)?.title;
  const countryName = (countriesState ?? []).find((c: any) => c.code === country)?.name;
  const countryFlag =
    country && country.length === 2
      ? String.fromCodePoint(...[...country.toUpperCase()].map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65))
      : '';
  const countryDisplay = countryName ? `${countryFlag} ${countryName}`.trim() : null;

  return (
    <Stack mb={4}>
      <SectionLabel first>General</SectionLabel>

      {/* spacing 0: the rows' own hairlines are the separator, so a gap between
          them would leave each rule floating in space instead of dividing. */}
      <Stack spacing={0}>
        <FieldRow
          label='Full name'
          value={fullName}
          placeholder='Add your name'
          onClick={() => onEditField({ key: 'name', title: 'Edit name' })}
        />
        <FieldRow
          label='User name'
          value={username}
          placeholder='Add username'
          onClick={() => onEditField({ key: 'username', title: 'Edit username' })}
        />
        <FieldRow
          label='Date of birth'
          value={birthday ? new Date(birthday).toLocaleDateString() : null}
          onClick={() => onEditField({ key: 'birthday', title: 'Date of birth' })}
        />
        <FieldRow
          label='Gender'
          value={genderLabel}
          onClick={() => onEditField({ key: 'gender', title: 'Gender' })}
        />
        <FieldRow
          label='Country'
          value={countryDisplay}
          onClick={() => onEditField({ key: 'country', title: 'Country' })}
        />
        <FieldRow
          label='Phone'
          value={phone}
          onClick={() => onEditField({ key: 'phone', title: 'Phone' })}
        />
      </Stack>
    </Stack>
  );
};
